"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import Link from "next/link";
import { Heart, Eye, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { MARQUEE_ITEMS, TESTIMONIALS } from "@/lib/data";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

declare global {
  interface Window {
    instgrm?: { Embeds: { process: () => void } };
  }
}

interface Ripple { x: number; y: number; t: number; }

/* ── component ─────────────────────────────────── */
export default function Home() {
  /* marquee */
  const [mOffset, setMOffset] = useState(0);
  const mRaf = useRef<number>(0);

  /* testimonials */
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  /* ── Trending products from DB ── */
  const [trendingProducts, setTrendingProducts] = useState<any[]>([]);
  const [loadingTrending, setLoadingTrending] = useState(true);
  const [trendingError, setTrendingError] = useState<string | null>(null);

  /* ── Custom Made Services: ALL fabrics, all categories, flat list ── */
  const [customFabrics, setCustomFabrics] = useState<any[]>([]);
  const [loadingCustomFabrics, setLoadingCustomFabrics] = useState(true);
  const [customFabricsError, setCustomFabricsError] = useState<string | null>(null);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) =>
      prev === TESTIMONIALS.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) =>
      prev === 0 ? TESTIMONIALS.length - 1 : prev - 1
    );
  };

  /* ripple canvas */
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ripplesRef = useRef<Ripple[]>([]);
  const animRef = useRef<number>(0);
  const lastMoveRef = useRef(0);

  /* form state */
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", address: "", date: "", slot: "" });
  const [isBooked, setIsBooked] = useState(false);

  /* ── marquee RAF ── */
  useEffect(() => {
    let pos = 0;
    const go = () => { pos -= 0.45; setMOffset(pos); mRaf.current = requestAnimationFrame(go); };
    mRaf.current = requestAnimationFrame(go);
    return () => cancelAnimationFrame(mRaf.current);
  }, []);

  /* ── Testimonial Auto-timer ── */
  useEffect(() => {
    const timer = setInterval(() => {
      nextTestimonial();
    }, 4800);
    return () => clearInterval(timer);
  }, []);

  /* ── Fetch Trending Products from DB ── */
  useEffect(() => {
    const fetchTrending = async () => {
      try {
        setLoadingTrending(true);
        const res = await fetch(`/api/products?trending=true&limit=12`);
        const data = await res.json();
        if (data.success) {
          setTrendingProducts(data.data);
        } else {
          setTrendingError(data.message || "Failed to load trending products.");
        }
      } catch (err) {
        console.error("[fetchTrending]", err);
        setTrendingError("Could not connect to server.");
      } finally {
        setLoadingTrending(false);
      }
    };
    fetchTrending();
  }, []);

  /* ── Fetch ALL fabrics across every category, merge into one flat list ── */
  useEffect(() => {
    const categories = ["men", "women", "kids", "corporate"] as const;

    const fetchAllFabrics = async () => {
      try {
        setLoadingCustomFabrics(true);
        setCustomFabricsError(null);

        const results = await Promise.all(
          categories.map(async (cat) => {
            const res = await fetch(`/api/fabrics?category=${cat}&limit=50`);
            const data = await res.json();
            if (data.success && Array.isArray(data.data)) {
              return data.data.map((f: any) => ({ ...f, category: cat }));
            }
            return [];
          })
        );

        setCustomFabrics(results.flat());
      } catch (err) {
        console.error("[fetchAllFabrics]", err);
        setCustomFabricsError("Could not load custom made services.");
      } finally {
        setLoadingCustomFabrics(false);
      }
    };

    fetchAllFabrics();
  }, []);

  const categoryLabel = (cat: string) =>
    cat.charAt(0).toUpperCase() + cat.slice(1);

  // custom made carousel
  const [startIndex, setStartIndex] = useState(0);
  const nextSlide = useCallback(() => {
    setStartIndex((prev) =>
      prev + 3 < customFabrics.length ? prev + 1 : 0
    );
  }, [customFabrics.length]);

  const prevSlide = useCallback(() => {
    setStartIndex((prev) =>
      prev > 0 ? prev - 1 : Math.max(0, customFabrics.length - 3)
    );
  }, [customFabrics.length]);

  const [trendingIndex, setTrendingIndex] = useState(0);
  const nextTrending = useCallback(() => {
    setTrendingIndex((prev) =>
      prev + 1 >= trendingProducts.length ? 0 : prev + 1
    );
  }, [trendingProducts.length]);
  const prevTrending = useCallback(() => {
    setTrendingIndex((prev) =>
      prev - 1 < 0 ? Math.max(0, trendingProducts.length - 1) : prev - 1
    );
  }, [trendingProducts.length]);

  useEffect(() => {
    if (trendingProducts.length <= 3) return; // nothing to rotate through
    const timer = setInterval(() => {
      nextTrending();
    }, 4200);
    return () => clearInterval(timer);
  }, [trendingProducts.length, trendingIndex, nextTrending]);

  /* ── AUTOPLAY: Custom Made Services ──
     Same restart-on-change pattern as Trending Items above. */
  useEffect(() => {
    if (customFabrics.length <= 3) return; // nothing to rotate through
    const timer = setInterval(() => {
      nextSlide();
    }, 4500);
    return () => clearInterval(timer);
  }, [customFabrics.length, startIndex, nextSlide]);

  // instagram videos
  useEffect(() => {
    if (window?.instgrm) {
      window.instgrm.Embeds.process();
    }
  }, []);

  /* ── ripple canvas draw loop ── */
const spawnRipple = useCallback((clientX: number, clientY: number) => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const rect = canvas.getBoundingClientRect();

  ripplesRef.current.push({
    x: clientX - rect.left,
    y: clientY - rect.top,
    t: performance.now(),
  });
}, []);

const handleClick = useCallback((e: React.MouseEvent) => {
  spawnRipple(e.clientX, e.clientY);
}, [spawnRipple]);

const handleMove = useCallback((e: React.MouseEvent) => {
  const now = performance.now();

  if (now - lastMoveRef.current < 50) return;
  lastMoveRef.current = now;

  spawnRipple(e.clientX, e.clientY);
}, [spawnRipple]);

const handleTouch = useCallback((e: React.TouchEvent) => {
  if (e.touches[0]) {
    spawnRipple(e.touches[0].clientX, e.touches[0].clientY);
  }
}, [spawnRipple]);

useEffect(() => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const setSize = () => {
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
  };

  setSize();
  window.addEventListener("resize", setSize);

  const draw = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const now = performance.now();

    ripplesRef.current = ripplesRef.current.filter((ripple) => {
      const age = now - ripple.t;

if (age > 600) return false;

const baseRadius = age * 0.25;
const opacity = 1 - age / 600;

// waves count
for (let i = 0; i < 3; i++) {
  const radius = baseRadius + i * 8;

  ctx.beginPath();

  // imperfect circle (water feel)
  for (let a = 0; a < Math.PI * 2; a += 0.2) {
    const distortion = Math.sin(a * 6 + age * 0.01) * 2; 
    const x = ripple.x + (radius + distortion) * Math.cos(a);
    const y = ripple.y + (radius + distortion) * Math.sin(a);

    if (a === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }

  ctx.closePath();

  ctx.strokeStyle = `rgba(255,255,255,${opacity * (0.4 - i * 0.1)})`;
  ctx.lineWidth = 1;
  ctx.stroke();
}
      
      return true;
    });

    animRef.current = requestAnimationFrame(draw);
  };

  draw();

  return () => {
    cancelAnimationFrame(animRef.current);
    window.removeEventListener("resize", setSize);
  };
}, []);

  /* marquee loop offset */
  const ITEM_W = 265;
  const TOTAL_W = MARQUEE_ITEMS.length * ITEM_W;
  const loopOffset = (((-mOffset) % TOTAL_W) + TOTAL_W) % TOTAL_W;

  /* Form data */
  const handleBooking = async (e: React.FormEvent) => {
    e.preventDefault();

    const slotMap: Record<string, { start_time: string; end_time: string }> = {
      "10-11 AM": { start_time: "10:00:00", end_time: "11:00:00" },
      "11-12 PM": { start_time: "11:00:00", end_time: "12:00:00" },
      "12-01 PM": { start_time: "12:00:00", end_time: "13:00:00" },
    };

    const times = slotMap[formData.slot];

    const payload = {
      name: formData.name,
      date: formData.date,
      start_time: times?.start_time,
      end_time: times?.end_time,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
    };

    try {
      const res = await fetch("http://31.97.230.211:3002/api/appointments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.success) {
        setIsBooked(true);
      } else {
        alert("Booking failed: " + result.message);
      }
    } catch (err) {
      console.error("API Error:", err);
      alert("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=DM+Mono:wght@300;400&display=swap');
      `}</style>

      <main className="bg-[#fdfbf8] text-[#1a1a1a] font-['Cormorant_Garamond',Georgia,serif] min-h-screen overflow-x-hidden">

        {/* HERO */}
        <section
          className="relative min-h-screen flex items-center justify-center overflow-hidden px-6 md:px-20 py-24"
          onMouseMove={handleMove}
          onMouseDown={handleClick}
          onTouchStart={handleTouch}
        >
          <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
            <video
              autoPlay
              muted
              loop
              playsInline
              webkit-playsinline="true"
              preload="auto"
              className="w-full h-full object-cover opacity-80 scale-[1.5] md:scale-[1.2] lg:scale-[1.1]"
            >
              <source src="/home.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="absolute inset-0 bg-black/5 md:bg-transparent" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/60" />

          {/* Ripple Canvas */}
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-[2]" />

          {/* CONTENT GRID */}
          <div className="relative z-[10] w-full max-w-[1200px] grid lg:grid-cols-2 gap-10 items-center">
            {/* Left: Hero Text */}
            <div className="flex flex-col items-start gap-0">
              <div className="flex flex-col leading-[0.7] font-serif italic text-white uppercase">
                <span className="text-[clamp(3rem,8vw,7rem)] tracking-[-0.04em]">
                  ModisH
                </span>
                <span className="text-[clamp(3rem,8vw,7rem)] tracking-[-0.04em] text-[#c8a96e]">
                  KreationS
                </span>
                <span className="text-[clamp(3rem,8vw,7rem)] tracking-[-0.04em] mt-2">
                  FashioN
                </span>
                <span className="text-[clamp(3rem,8vw,7rem)] tracking-[-0.04em] text-[#c8a96e]">
                  HousE
                </span>
              </div>
              <div className="w-12 h-[2px] bg-[#c8a96e] mt-6" />
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 top-10 h-px bg-gradient-to-r from-transparent via-black/10 to-transparent" />
        </section>

        {/* Shop statement */}
        <section id="studio" className="max-w-[1200px] mx-auto px-6 md:px-14 py-20 bg-[#faf9f6]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">

            <div className="flex justify-center md:justify-end">
              <div className="relative p-2 border border-[#b8955a]/40">
                <div className="w-full max-w-[450px] overflow-hidden">
                  <img
                    src="/owner.png"
                    alt="Laxmi Bhadauria"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>

            {/* RIGHT SIDE: CONTENT */}
            <div className="flex flex-col space-y-6">
              <div className="space-y-2">
                <h2 className="text-[2.8rem] md:text-[3.5rem] font-serif tracking-wide text-[#121826] uppercase leading-tight">
                  Meet The Visionary
                </h2>
                <p className="text-[#121826] tracking-[0.2em] font-medium text-sm uppercase">
                  Laxmi Bhadauria | Founder, Modish Kreations
                </p>
              </div>

              <div className="space-y-6 max-w-[550px]">
                <p className="text-[#333] leading-[1.8] text-[1.05rem] font-light">
                  Laxmi Bhadauria, the creative force behind Modish Kreations, is a
                  visionary fashion designer with a postgraduate background and
                  formal training in fashion design. With over Eleven years of dedicated
                  experience, she brings a refined sense of aesthetic, precision, and
                  craftsmanship to every creation.
                </p>

                <p className="text-[#333] leading-[1.8] text-[1.05rem] font-light">
                  Her work reflects a deep passion for bespoke tailoring, where each
                  garment is thoughtfully designed to embody elegance, individuality,
                  and timeless sophistication—ensuring every client experiences true
                  luxury and confidence.
                </p>
              </div>
              <div className="pt-4">
                <a
                  href="/about"
                  className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-[#a8865c] to-[#c5a681] text-white text-[12px] font-bold uppercase tracking-[0.15em] rounded-sm hover:opacity-90 transition-opacity shadow-md"
                >
                  Discover More <span className="ml-2 text-lg"></span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── TRENDING ITEMS SECTION (DB-connected) ── */}
        <section className="py-12 px-6 md:py-16 bg-[#fafafa] overflow-hidden">
          <div className="max-w-[1200px] mx-auto relative">

            {/* Clickable Heading */}
            <div className="flex justify-center mb-10">
              <Link href="" className="group flex flex-col items-center gap-2">
                <h2 className="text-[2.5rem] font-light italic text-[#121826] group-hover:text-[#c8a96e] transition-colors duration-300">
                  Trending Items
                </h2>
                <div className="w-12 h-[1px] bg-[#c8a96e] group-hover:w-24 transition-all duration-500"></div>
              </Link>
            </div>

            {/* Loading Skeleton */}
            {loadingTrending && (
              <div className="flex gap-6 overflow-hidden">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex-shrink-0 w-[280px] sm:w-[300px] lg:w-[340px] animate-pulse">
                    <div className="w-full h-[360px] sm:h-[380px] lg:h-[420px] bg-gray-200 rounded-3xl mb-4" />
                    <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                ))}
              </div>
            )}

            {/* Error State */}
            {!loadingTrending && trendingError && (
              <div className="text-center py-16 text-[#b8955a] italic font-serif text-lg">
                {trendingError}
              </div>
            )}

            {/* Empty State */}
            {!loadingTrending && !trendingError && trendingProducts.length === 0 && (
              <div className="text-center py-16 text-gray-400 italic font-serif text-lg">
                No trending products at the moment.
              </div>
            )}

            {/* Carousel */}
            {!loadingTrending && !trendingError && trendingProducts.length > 0 && (
              <div className="relative group px-2">

                {/* NAVIGATION BUTTONS */}
                <div className="absolute top-1/2 -translate-y-1/2 -left-4 md:-left-8 z-30">
                  <button
                    onClick={prevTrending}
                    className="p-3 bg-white rounded-full shadow-lg hover:bg-[#121826] hover:text-white transition-all border border-gray-50"
                  >
                    <ChevronLeft size={24} />
                  </button>
                </div>

                <div className="absolute top-1/2 -translate-y-1/2 -right-4 md:-right-8 z-30">
                  <button
                    onClick={nextTrending}
                    className="p-3 bg-white rounded-full shadow-lg hover:bg-[#121826] hover:text-white transition-all border border-gray-50"
                  >
                    <ChevronRight size={24} />
                  </button>
                </div>

                {/* CAROUSEL TRACK */}
                <div className="overflow-hidden">
                  <div
                    className="flex transition-transform duration-500 ease-out gap-6"
                    style={{ transform: `translateX(-${trendingIndex * (340 + 24)}px)` }}
                  >
                    {trendingProducts.map((product) => {
                      const primaryImage =
                        product.media?.find((m: any) => m.is_primary)?.src ||
                        product.media?.[0]?.src ||
                        "/placeholder-product.jpg";

                      const viewLink = `/product/${product.slug || product.id}`;

                      const hasDiscount =
                        product.sale_price &&
                        Number(product.sale_price) < Number(product.regular_price);

                      const discountPct = hasDiscount
                        ? Math.round(
                            ((Number(product.regular_price) - Number(product.sale_price)) /
                              Number(product.regular_price)) *
                              100
                          )
                        : null;

                      return (
                        <Link
                          key={product.id}
                          href={viewLink}
                          className="flex-shrink-0 w-[280px] sm:w-[300px] lg:w-[340px] group cursor-pointer block"
                        >
                          {/* IMAGE */}
                          <div className="relative w-full h-[360px] sm:h-[380px] lg:h-[420px] bg-[#e5e5e5] rounded-3xl overflow-hidden mb-4">
                            {discountPct && (
                              <span className="absolute top-4 left-0 bg-[#121826] text-white text-[10px] font-bold px-3 py-1.5 rounded-r-full z-10">
                                -{discountPct}%
                              </span>
                            )}

                            {product.is_new ? (
                              <span className="absolute top-4 right-4 bg-[#c8a96e] text-white text-[9px] font-bold px-2 py-1 rounded-full z-10 uppercase tracking-wider">
                                New
                              </span>
                            ) : null}

                            {/* Action Icons */}
                            <div className="absolute top-4 right-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-4 group-hover:translate-x-0 z-30">
                              <button
                                className="p-2 bg-white rounded-full shadow-md hover:bg-black hover:text-white transition-colors"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                              >
                                <Heart size={16} />
                              </button>
                              <div className="p-2 bg-white rounded-full shadow-md hover:bg-black hover:text-white transition-colors">
                                <Eye size={16} />
                              </div>
                            </div>

                            <div className="h-full w-full">
                              <img
                                src={primaryImage}
                                alt={product.name}
                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                              />
                            </div>
                          </div>

                          {/* Product Details */}
                          <div className="space-y-1 px-2">
                            {product.rating_avg > 0 && (
                              <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <span
                                    key={i}
                                    className={
                                      i < Math.round(product.rating_avg)
                                        ? "text-red-600"
                                        : "text-gray-300"
                                    }
                                  >
                                    ★
                                  </span>
                                ))}
                                <span className="text-[11px] text-gray-400 ml-1">
                                  ({product.review_count || 0})
                                </span>
                              </div>
                            )}

                            <div className="flex justify-between items-start gap-4">
                              <h3 className="text-sm font-bold text-gray-800 leading-tight flex-1">
                                {product.name}
                              </h3>
                              <div className="text-right">
                                {hasDiscount && (
                                  <p className="text-[11px] text-gray-400 line-through">
                                    ₹{Number(product.regular_price).toLocaleString("en-IN")}
                                  </p>
                                )}
                                <p className="text-sm font-bold text-gray-900">
                                  ₹{Number(
                                    product.sale_price || product.regular_price
                                  ).toLocaleString("en-IN")}
                                </p>
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {/* Progress Dots */}
                {trendingProducts.length > 3 && (
                  <div className="flex justify-center gap-2 mt-8">
                    {trendingProducts.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setTrendingIndex(index)}
                        className={`h-[3px] transition-all duration-300 rounded-full ${
                          trendingIndex === index
                            ? "w-8 bg-[#b8955a]"
                            : "w-2 bg-gray-300 hover:bg-gray-400"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ── CUSTOM MADE SERVICES (all fabrics, all categories, flat list) ── */}
        <section className="py-12 px-6 md:py-16 bg-[#fafafa] overflow-hidden">
          <div className="max-w-[1200px] mx-auto relative">

            {/* HEADING */}
            <div className="text-center mb-14">
              <h2 className="text-[2.5rem] font-light italic text-[#121826]">
                Custom Made Services
              </h2>
              <div className="w-16 h-[1px] bg-[#c8a96e] mx-auto mt-2"></div>
            </div>

            {/* Loading Skeleton */}
            {loadingCustomFabrics && (
              <div className="flex gap-6 overflow-hidden px-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="min-w-[100%] sm:min-w-[calc(50%-12px)] lg:min-w-[calc(33.333%-16px)] animate-pulse">
                    <div className="w-full aspect-[336/400] bg-gray-200 rounded-[2rem] mb-4" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                ))}
              </div>
            )}

            {/* Error State */}
            {!loadingCustomFabrics && customFabricsError && (
              <div className="text-center py-16 text-[#b8955a] italic font-serif text-lg">
                {customFabricsError}
              </div>
            )}

            {/* Empty State */}
            {!loadingCustomFabrics && !customFabricsError && customFabrics.length === 0 && (
              <div className="text-center py-16 text-gray-400 italic font-serif text-lg">
                No custom made services available right now.
              </div>
            )}

            {/* CAROUSEL CONTAINER */}
            {!loadingCustomFabrics && !customFabricsError && customFabrics.length > 0 && (
              <div className="relative group/carousel px-4">

                {/* NAVIGATION BUTTONS */}
                <div className="absolute top-1/2 -translate-y-1/2 -left-2 md:-left-8 z-30">
                  <button
                    onClick={prevSlide}
                    className="p-3 bg-white rounded-full shadow-lg hover:bg-[#121826] hover:text-white transition-all duration-300 border border-gray-100"
                  >
                    <ChevronLeft size={24} />
                  </button>
                </div>

                <div className="absolute top-1/2 -translate-y-1/2 -right-2 md:-right-8 z-30">
                  <button
                    onClick={nextSlide}
                    className="p-3 bg-white rounded-full shadow-lg hover:bg-[#121826] hover:text-white transition-all duration-300 border border-gray-100"
                  >
                    <ChevronRight size={24} />
                  </button>
                </div>

                {/* CAROUSEL TRACK */}
                <div className="overflow-hidden">
                  <div
                    className="flex transition-transform duration-500 ease-out gap-6"
                    style={{ transform: `translateX(-${startIndex * (100 / (customFabrics.length > 2 ? 3 : 1) + 1.2)}%)` }}
                  >
                    {customFabrics.map((fabric, idx) => {
                      const viewLink = `/gallery/${fabric.category}T/#fabric-library`;
                      const tailoringLabel = `${categoryLabel(fabric.category)} Tailoring`;
                      const title = `${fabric.name} ${tailoringLabel}`; // used for alt text only

                      return (
                        <div
                          key={fabric.id ?? `${fabric.category}-${idx}`}
                          className="min-w-[100%] sm:min-w-[calc(50%-12px)] lg:min-w-[calc(33.333%-16px)] group cursor-pointer"
                        >
                          {/* IMAGE CONTAINER */}
                          <Link
                            href={viewLink}
                            className="block relative aspect-[336/400] bg-[#e5e5e5] rounded-[2rem] overflow-hidden mb-4 shadow-sm"
                          >
                            {/* Category badge */}
                            <span className="absolute top-5 left-0 bg-[#121826] text-white text-[10px] font-bold tracking-widest px-4 py-2 rounded-r-full z-10 uppercase">
                              {categoryLabel(fabric.category)}
                            </span>

                            {/* Main image: fabric DB image or skeleton */}
                            <div className="h-full w-full">
                              {fabric.img ? (
                                <img
                                  src={fabric.img}
                                  alt={title}
                                  className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                />
                              ) : (
                                /* Loading skeleton while fabric image is missing */
                                <div className="h-full w-full bg-gradient-to-br from-[#ede8e0] to-[#d8cfc2] animate-pulse flex flex-col items-center justify-center gap-3">
                                  <div className="w-10 h-10 rounded-full border-2 border-[#c8a96e]/40 border-t-[#c8a96e] animate-spin" />
                                  <span className="text-[11px] tracking-[0.15em] text-[#b8955a] uppercase font-light">
                                    Loading fabric…
                                  </span>
                                </div>
                              )}
                            </div>
                          </Link>

                          {/* DETAILS SECTION */}
                          <div className="space-y-1 px-2">
                            <Link href={viewLink}>
                              <h3 className="text-lg text-[17px] font-bold text-gray-800 group-hover:text-[#c8a96e] transition-colors duration-300 leading-snug">
                                {fabric.name}
                              </h3>
                              <p className="text-sm text-[20px] font-medium text-[#b8955a] tracking-wide mt-0.5">
                                {tailoringLabel}
                              </p>
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* PROGRESS DOTS */}
                <div className="flex justify-center gap-2 mt-12">
                  {[...Array(Math.max(0, customFabrics.length - 2))].map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setStartIndex(index)}
                      className={`h-1 transition-all duration-300 rounded-full ${
                        startIndex === index ? "w-8 bg-[#b8955a]" : "w-2 bg-gray-300 hover:bg-gray-400"
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}

          </div>
        </section>

        {/* Testimonials */}
        <section className="bg-[#faf8f5] py-12 px-6 md:py-16">
          <div className="max-w-[1100px] mx-auto">

            {/* HEADER SECTION */}
            <div className="flex flex-col items-center mb-16 text-center">
              <h2 className="text-[2.5rem] md:text-[3.2rem] font-serif italic text-[#121826] leading-tight">
                Testimonials
              </h2>
              <div className="w-20 h-[1.5px] bg-[#c8a96e] mt-6"></div>
            </div>

            {/* MAIN CAROUSEL BOX */}
            <div className="relative max-w-[1000px] mx-auto">

              <div className="bg-white rounded-[2.5rem] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-[#f0ede8]">
                <div className="flex flex-col md:flex-row">

                  {/* IMAGE SECTION */}
                  <div className="w-full md:w-5/12 h-[350px] md:h-[500px] relative overflow-hidden group">
                    <div className="absolute inset-0 bg-black/5 z-10 group-hover:bg-transparent transition-colors duration-700"></div>
                    <img
                      key={currentTestimonial}
                      src={TESTIMONIALS[currentTestimonial].image}
                      alt={TESTIMONIALS[currentTestimonial].client}
                      className="w-full h-full object-cover animate-in fade-in zoom-in duration-1000"
                    />
                  </div>

                  {/* CONTENT SECTION */}
                  <div className="w-full md:w-7/12 p-10 md:p-16 flex flex-col justify-center relative">
                    <Quote className="absolute top-10 right-10 text-[#faf8f5] w-24 h-24 -z-0 rotate-12" />

                    <div className="relative z-10 space-y-8">
                      <p className="text-[1.3rem] md:text-[1.6rem] text-[#333] font-light leading-[1.8] italic font-serif">
                        "{TESTIMONIALS[currentTestimonial].text}"
                      </p>

                      <div className="pt-6 border-t border-[#f0ede8]">
                        <h4 className="text-[1.1rem] font-bold text-[#121826] tracking-wide uppercase">
                          {TESTIMONIALS[currentTestimonial].client}
                        </h4>
                        <p className="text-[#b8955a] text-[0.9rem] tracking-wider mt-1">
                          {TESTIMONIALS[currentTestimonial].role}
                        </p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* NAVIGATION */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-4 z-20">
                <button
                  onClick={prevTestimonial}
                  className="p-4 bg-white rounded-full shadow-md border border-[#eee] text-[#121826] hover:bg-[#121826] hover:text-white transition-all duration-300 active:scale-95"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={nextTestimonial}
                  className="p-4 bg-white rounded-full shadow-md border border-[#eee] text-[#121826] hover:bg-[#121826] hover:text-white transition-all duration-300 active:scale-95"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

            </div>

            {/* REFINED PROGRESS INDICATOR */}
            <div className="flex justify-center gap-3 mt-16">
              {TESTIMONIALS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`h-[3px] transition-all duration-500 rounded-full ${
                    currentTestimonial === index
                      ? "w-10 bg-[#b8955a]"
                      : "w-4 bg-[#e5e5e5] hover:bg-gray-300"
                  }`}
                />
              ))}
            </div>

          </div>
        </section>
      </main>

      {/* Keyframes for hero animations */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}